import { useCallback, useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import { Header, Form, Icon } from 'semantic-ui-react';
import { useMutation } from 'react-query';
import toast from 'react-hot-toast';
import { Button, Modal } from '~/components';
import { Table } from '~/components';
import { getInvestors } from '~/api/admin';
import {
  copyDealExclusions,
  createDealExclusion,
  DealCopyDropdownOption,
  getDealExclusions,
  removeDealExclusion,
  searchDealsForExclusionCopy,
  updateDealExclusionReason,
  updateDealExclusionNotes
} from '~/api/dealExclusions';
import './DealExclusionCard.scss';

type Props = {
  projectId?: string;
  portfolioId?: string;
};

export default function DealExclusionCard({ projectId, portfolioId }: Props) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [dealExclusions, setDealExclusions] = useState<any[]>([]);
  const [allInvestors, setAllInvestors] = useState<any[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [exclusionReason, setExclusionReason] = useState<string>('');
  const [dealExclusionNotes, setDealExclusionNotes] = useState<string>('');
  const [copySourceId, setCopySourceId] = useState('');
  const [copyDealOptions, setCopyDealOptions] = useState<DealCopyDropdownOption[]>([]);
  const [copySearchLoading, setCopySearchLoading] = useState(false);
  const [copyNotesToo, setCopyNotesToo] = useState(false);
  const [editingExclusionId, setEditingExclusionId] = useState<string | null>(null);

  const dealParams = useMemo(() => (
    projectId ? { projectId } : { portfolioId }
  ), [projectId, portfolioId]);

  const { mutate: fetchInvestors, status: fetchInvestorsStatus } = useMutation(
    async () => getInvestors(dealParams),
    { onSuccess: (data) => setAllInvestors(data || []) }
  );

  const { mutate: fetchExclusions, status: fetchExclusionsStatus } = useMutation(
    async () => getDealExclusions(dealParams),
    {
      onSuccess: (data) => {
        setDealExclusions(data.exclusions || []);
        setDealExclusionNotes(data.exclusionNotes ?? '');
      }
    }
  );

  const { mutateAsync: saveExclusionNotes, status: saveNotesStatus } = useMutation(
    async () => updateDealExclusionNotes({
      ...dealParams,
      exclusionNotes: dealExclusionNotes.trim() === '' ? null : dealExclusionNotes.trim()
    })
  );

  const { mutateAsync: addExclusion, status: addExclusionStatus } = useMutation(
    async () => {
      if (!selectedCompanyId) {
        throw new Error('Please select a company');
      }
      return createDealExclusion({
        companyId: selectedCompanyId,
        reason: exclusionReason || undefined,
        ...dealParams
      });
    }
  );

  const { mutateAsync: deleteExclusion, status: deleteExclusionStatus } = useMutation(
    async (exclusionId: string) => removeDealExclusion(exclusionId)
  );

  const { mutateAsync: saveExclusionReason, status: saveReasonStatus } = useMutation(
    async ({ id, reason }: { id: string; reason: string }) =>
      updateDealExclusionReason({
        id,
        reason: reason.trim() === '' ? null : reason.trim()
      })
  );

  const { mutateAsync: runCopyExclusions, status: copyExclusionsStatus } = useMutation(
    async () => {
      const targetId = projectId ?? portfolioId;
      if (!targetId) {
        throw new Error('Missing deal context');
      }
      const raw = copySourceId.trim();
      const sep = raw.indexOf('|');
      const kind = sep >= 0 ? raw.slice(0, sep) : '';
      const sourceId = sep >= 0 ? raw.slice(sep + 1) : '';
      if (!sourceId || (kind !== 'project' && kind !== 'portfolio')) {
        throw new Error('Select a source project or portfolio');
      }
      if (kind === 'project' && projectId && sourceId === projectId) {
        throw new Error('Choose a different deal than this one');
      }
      if (kind === 'portfolio' && portfolioId && sourceId === portfolioId) {
        throw new Error('Choose a different deal than this one');
      }
      const payload: Parameters<typeof copyDealExclusions>[0] = {
        copyNotes: copyNotesToo
      };
      if (kind === 'project') {
        payload.fromProjectId = sourceId;
      } else {
        payload.fromPortfolioId = sourceId;
      }
      if (projectId) {
        payload.toProjectId = targetId;
      } else {
        payload.toPortfolioId = targetId;
      }
      return copyDealExclusions(payload);
    }
  );

  const loadCopyOptions = useCallback(
    async (q: string) => {
      const term = q.trim().replace(/[%_]/g, '');
      if (!term) {
        setCopyDealOptions([]);
        return;
      }
      if (!projectId && !portfolioId) {
        setCopyDealOptions([]);
        return;
      }
      setCopySearchLoading(true);
      try {
        setCopyDealOptions(
          await searchDealsForExclusionCopy(term, {
            excludeProjectId: projectId,
            excludePortfolioId: portfolioId
          })
        );
      } catch {
        setCopyDealOptions([]);
        toast.error('Could not load deals to copy from', { duration: 3000 });
      } finally {
        setCopySearchLoading(false);
      }
    },
    [projectId, portfolioId]
  );

  const debouncedLoadCopyOptions = useMemo(
    () => debounce((q: string) => { void loadCopyOptions(q); }, 300),
    [loadCopyOptions]
  );

  useEffect(() => () => debouncedLoadCopyOptions.cancel(), [debouncedLoadCopyOptions]);

  const resetCopyModal = () => {
    setIsCopyModalOpen(false);
    setCopySourceId('');
    setCopyDealOptions([]);
    setCopyNotesToo(false);
    setCopySearchLoading(false);
    debouncedLoadCopyOptions.cancel();
  };

  useEffect(() => {
    fetchExclusions();
    fetchInvestors();
  }, [projectId, portfolioId]);

  const investorOptions = allInvestors
    .map((investor: any) => {
      const flags: string[] = [];
      if (investor.isExcludedForDeal) flags.push('Excluded');
      if (investor.hasRestrictedAccess) flags.push('Restricted access');
      return {
        key: investor.company?.id,
        value: investor.company?.id,
        text: flags.length
          ? `${investor.company?.name} (${flags.join(', ')})`
          : investor.company?.name
      };
    })
    .filter((option: any) => !!option.value);

  const resetExclusionModal = () => {
    setIsModalOpen(false);
    setModalMode('create');
    setEditingExclusionId(null);
    setSelectedCompanyId('');
    setExclusionReason('');
  };

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setEditingExclusionId(null);
    setSelectedCompanyId('');
    setExclusionReason('');
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (row: any) => {
    setModalMode('edit');
    setEditingExclusionId(row.id);
    setSelectedCompanyId(row.company?.id || '');
    setExclusionReason(row.reason || '');
    setIsModalOpen(true);
  };

  const handleSaveNotes = async () => {
    try {
      await saveExclusionNotes();
      toast.success('Saved exclusion notes', { duration: 3000 });
    } catch (err: any) {
      toast.error(err?.message || 'Could not save notes', { duration: 4000 });
    }
  };

  const handleCopyExclusions = async () => {
    try {
      const result = await runCopyExclusions();
      const { inserted, skipped } = result;
      const parts: string[] = [];
      if (inserted > 0) {
        parts.push(`Added ${inserted} exclusion${inserted === 1 ? '' : 's'}`);
      }
      if (skipped > 0) {
        parts.push(`${skipped} already listed here`);
      }
      if (copyNotesToo) {
        parts.push('Exclusion notes updated from source');
      }
      if (parts.length === 0) {
        parts.push('No exclusions on the source deal to copy');
      }
      toast.success(parts.join('. ') + '.', { duration: 5000 });
      resetCopyModal();
      fetchExclusions();
      fetchInvestors();
    } catch (err: any) {
      toast.error(err?.message || 'Could not copy exclusions', { duration: 4000 });
    }
  };

  const handleRemoveExclusion = async (exclusionId: string) => {
    try {
      await deleteExclusion(exclusionId);
      fetchExclusions();
      fetchInvestors();
      toast.success('Removed exclusion', { duration: 4000 });
    } catch (err: any) {
      toast.error(err?.message || 'Could not remove exclusion', { duration: 4000 });
    }
  };

  const handleSubmitExclusionModal = async () => {
    try {
      if (modalMode === 'edit') {
        if (!editingExclusionId) return;
        await saveExclusionReason({ id: editingExclusionId, reason: exclusionReason });
        toast.success('Updated exclusion reason', { duration: 3000 });
      } else {
        await addExclusion();
        toast.success('Added company to exclusion list', { duration: 4000 });
      }
      resetExclusionModal();
      fetchExclusions();
      fetchInvestors();
    } catch (err: any) {
      toast.error(
        err?.message ||
        (modalMode === 'edit' ? 'Could not update exclusion reason' : 'Could not add exclusion'),
        { duration: 4000 }
      );
    }
  };

  return (
    <div className="card">
      <Header as='h2' style={{ margin: 0, marginBottom: '12px' }}>Deal exclusion list</Header>

      {fetchExclusionsStatus === 'loading' ? (
        <p>Loading exclusions...</p>
      ) : dealExclusions.length === 0 ? (
        <p style={{ color: '#666' }}>No excluded companies for this deal.</p>
      ) : (
        <div className="summary-table-container deal-exclusion-table-container">
          <Table className="deal-exclusion-table" compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell color='grey'>Company</Table.HeaderCell>
                <Table.HeaderCell color='grey'>Reason</Table.HeaderCell>
                <Table.HeaderCell color='grey' textAlign='right'>Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {dealExclusions.map((row: any) => (
                <Table.Row key={row.id}>
                  <Table.Cell>{row.company?.name || 'Unknown company'}</Table.Cell>
                  <Table.Cell>{row.reason || '-'}</Table.Cell>
                  <Table.Cell textAlign='right'>
                    <>
                      <Button
                        primary
                        outlined
                        size="small"
                        style={{ marginRight: '8px' }}
                        disabled={deleteExclusionStatus === 'loading'}
                        onClick={() => handleOpenEditModal(row)}
                      >
                        Edit
                      </Button>
                      <Button
                        primary
                        outlined
                        size="small"
                        loading={deleteExclusionStatus === 'loading'}
                        onClick={() => handleRemoveExclusion(row.id)}
                      >
                        Remove
                      </Button>
                    </>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '16px', alignItems: 'center' }}>
        <Button
          primary
          onClick={handleOpenCreateModal}
        >
          <Icon name='plus' />
          Add exclusion
        </Button>
        <Button
          primary
          outlined
          onClick={() => {
            setCopySourceId('');
            setCopyDealOptions([]);
            setCopyNotesToo(false);
            setIsCopyModalOpen(true);
          }}
        >
          <Icon name='copy outline' />
          Copy from another deal
        </Button>
      </div>

      <div className="deal-exclusion-notes-section">
        <Header as="h3" style={{ marginTop: '24px', marginBottom: '8px' }}>Exclusion notes</Header>
        <p style={{ color: '#666', fontSize: '13px', marginBottom: '10px' }}>
          Internal notes for this deal&apos;s exclusions (not shown to buyers).
        </p>
        <Form>
          <Form.TextArea
            placeholder="Add context on exclusion policy, exceptions, or follow-ups…"
            value={dealExclusionNotes}
            onChange={(_e, { value }) => setDealExclusionNotes(String(value ?? ''))}
            rows={4}
          />
          <Button
            primary
            size="small"
            style={{ marginTop: '10px' }}
            loading={saveNotesStatus === 'loading'}
            disabled={saveNotesStatus === 'loading' || fetchExclusionsStatus === 'loading'}
            onClick={() => void handleSaveNotes()}
          >
            Save notes
          </Button>
        </Form>
      </div>

      {isCopyModalOpen && (
        <Modal closeModal={resetCopyModal}>
          <Modal.Title>Copy exclusions from another deal</Modal.Title>
          <Modal.Content>
            <Form>
              <p style={{ color: '#666', fontSize: '13px', marginBottom: '12px' }}>
                Search for a project or portfolio to copy exclusions from. Companies already excluded on this deal are skipped.
              </p>
              <Form.Dropdown
                selection
                search={(opts) => opts}
                fluid
                label="Source deal"
                placeholder="Type to search by project or portfolio name…"
                options={copyDealOptions}
                loading={copySearchLoading}
                value={copySourceId}
                onChange={(_e, { value }) => setCopySourceId(String(value ?? ''))}
                onSearchChange={(_e, { searchQuery }) =>
                  debouncedLoadCopyOptions(String(searchQuery ?? ''))
                }
              />
              <Form.Checkbox
                label="Also copy exclusion notes to this deal"
                checked={copyNotesToo}
                onChange={(_e, { checked }) => setCopyNotesToo(Boolean(checked))}
              />
            </Form>
          </Modal.Content>
          <Modal.Footer>
            <Button
              primary
              outlined
              onClick={resetCopyModal}
            >
              Cancel
            </Button>
            <Button
              primary
              loading={copyExclusionsStatus === 'loading'}
              disabled={copyExclusionsStatus === 'loading' || !copySourceId.trim()}
              onClick={() => void handleCopyExclusions()}
            >
              Copy exclusions
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {isModalOpen && (
        <Modal closeModal={resetExclusionModal}>
          <Modal.Title>{modalMode === 'edit' ? 'Edit exclusion reason' : 'Add exclusion'}</Modal.Title>
          <Modal.Content>
            <Form>
              <Form.Dropdown
                selection
                search
                label='Company'
                options={investorOptions}
                placeholder='Choose a company...'
                value={selectedCompanyId}
                disabled={modalMode === 'edit'}
                loading={fetchInvestorsStatus === 'loading'}
                onChange={(_e, { value }) => setSelectedCompanyId((value as string) || '')}
              />
              <Form.TextArea
                label='Reason (optional)'
                placeholder='Why is this company excluded from this deal?'
                value={exclusionReason}
                onChange={(_e, { value }) => setExclusionReason(String(value || ''))}
              />
              <p style={{ marginTop: '8px', color: '#666', fontSize: '12px' }}>
                Exclusions affect listing visibility. Admin invitations still override exclusions.
              </p>
            </Form>
          </Modal.Content>
          <Modal.Footer>
            <Button primary outlined onClick={resetExclusionModal}>
              Close
            </Button>
            <Button
              primary
              loading={modalMode === 'edit' ? saveReasonStatus === 'loading' : addExclusionStatus === 'loading'}
              disabled={
                modalMode === 'edit'
                  ? !editingExclusionId || saveReasonStatus === 'loading'
                  : !selectedCompanyId || addExclusionStatus === 'loading'
              }
              onClick={() => void handleSubmitExclusionModal()}
            >
              {modalMode === 'edit' ? 'Save reason' : 'Add exclusion'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}
